/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import defaultTheme from 'tailwindcss/defaultTheme';
import type { ShopperSearch } from '@/scapi';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig, BadgeDetail } from '@/types/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { getProductBadges } from '@/lib/product/product-badges';
import type { ProductUrlMap } from '@/lib/product/product-utils';
import { resourceRoutes } from '@/route-paths';
import type { RepresentedPricesResult } from '@/routes/resource.represented-prices';

/**
 * RGBARRY-CUSTOMIZATION: per-id state of a master tile's represented (face-out) variant price.
 * - `undefined` — not yet resolved (the batched fetch hasn't returned for this id): show a skeleton.
 * - `RepresentedPrice` — the represented variant's effective and optional list price: show it.
 * - `null` — resolved but the id has no price for the active currency, or the fetch failed: fall
 *   back to the master price (today's behavior) rather than a skeleton that never fills.
 */
export type RepresentedPriceMap = Record<string, RepresentedPricesResult['prices'][string] | null>;

type ProductBadgesResult = { badges: BadgeDetail[]; hasBadges: boolean };

interface ProductTileContextValue {
    navigate: NavigateFunction;
    config: AppConfig;
    t: TFunction<'product'>;
    currency?: string;
    getBadges: (product: ShopperSearch.schemas['ProductSearchHit']) => ProductBadgesResult;
    /**
     * RGBARRY-CUSTOMIZATION (W-23875749): `productId -> canonical slug path` resolved by
     * `int_urlresolver_scapi` for the tiles in this grid. A tile prefers this over its local slugify
     * derivation so the link is byte-identical to production. Empty when the resolver hasn't been wired
     * for the surface, hasn't streamed in yet, or degraded — tiles fall back to the slugify path.
     */
    resolvedProductPaths: ProductUrlMap;
    /**
     * RGBARRY-CUSTOMIZATION: when true, master tiles in this grid display their *represented* (face-out)
     * variant's price instead of the master's minimum price. A tile registers its represented id via
     * `requestRepresentedPrice` and reads the resolved value from `representedPrices`. Off everywhere by
     * default — only the surfaces that opt in (e.g. the category PLP) pay for the extra batched call.
     */
    enableRepresentedPrices: boolean;
    /**
     * RGBARRY-CUSTOMIZATION: when true, master tiles use Shopper Search's query-matching
     * `representedProduct` instead of the merchant default for image, link, and represented price.
     * Category PLPs enable this only while a price refinement is active so the visible variant
     * explains why its master qualified for the selected range.
     */
    preferSearchRepresentedVariant: boolean;
    /**
     * RGBARRY-CUSTOMIZATION: `representedVariantId -> price state` for the master tiles in this grid.
     * See {@link RepresentedPriceMap}: `undefined` = still loading, a price object = show it,
     * `null` = resolved-missing/failed (fall back to the master price). Always `{}` when
     * `enableRepresentedPrices` is false.
     */
    representedPrices: RepresentedPriceMap;
    /**
     * RGBARRY-CUSTOMIZATION: register a represented variant id to be priced. Idempotent and batched —
     * many tiles calling this in the same tick coalesce into one `/resource/represented-prices` request.
     * No-op when `enableRepresentedPrices` is false.
     */
    requestRepresentedPrice: (id: string) => void;
}

const ProductTileContext = createContext<ProductTileContextValue | null>(null);

/** Stable empty map so tiles without a resolver-populated provider share one reference (no re-render churn). */
const EMPTY_PRODUCT_URL_MAP: ProductUrlMap = {};

const DESKTOP_QUERY = `(min-width: ${defaultTheme.screens.lg})`;

/**
 * Whether the viewport is at or above tailwind's `lg` breakpoint (desktop device). Read this at event time (e.g. from a
 * swatch-hover handler), never during render: it depends on `matchMedia`, which is unavailable during SSR and returns a
 * different value on the client, so reading it while rendering causes a hydration mismatch and a post-hydration
 * re-render. On the server (no `matchMedia`) it returns `false`.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function isDesktopViewport(): boolean {
    return globalThis.matchMedia?.(DESKTOP_QUERY)?.matches ?? false;
}

/**
 * Provider that initializes shared hooks once for all product tiles, e.g., displayed in a product grid.
 * This reduces hydration overhead by avoiding repeated hook initialization for each tile,
 * (e.g., 24 tiles = 96x hook calls reduced to 24x context hook calls + the hooks in the provider).
 */
/** Stable empty represented-price map so disabled/absent providers share one reference. */
const EMPTY_REPRESENTED_PRICES: RepresentedPriceMap = {};

/** No-op registration used when represented pricing is off, so consumers can call it unconditionally. */
const NOOP_REQUEST_REPRESENTED_PRICE = (_id: string): void => undefined;

export function ProductTileProvider({
    resolvedProductPaths,
    enableRepresentedPrices = false,
    preferSearchRepresentedVariant = false,
    children,
}: PropsWithChildren<{
    resolvedProductPaths?: ProductUrlMap;
    enableRepresentedPrices?: boolean;
    preferSearchRepresentedVariant?: boolean;
}>) {
    const navigate = useNavigate();
    const config = useConfig();
    const { t } = useTranslation('product');
    const { currency } = useSite();
    const getBadges = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) =>
            getProductBadges({ product, badgeDetails: config.global.badges, maxBadges: 2 }),
        [config.global.badges]
    );
    // RGBARRY-CUSTOMIZATION (W-23875749): keep a stable empty-map reference when the resolver map is absent so
    // the memo below (and every tile) doesn't churn on each render. Stringify as the dep so a same-content map
    // from a loader revalidation doesn't force a re-render either.
    const paths = resolvedProductPaths ?? EMPTY_PRODUCT_URL_MAP;
    const pathsKey = resolvedProductPaths ? JSON.stringify(resolvedProductPaths) : '';

    // RGBARRY-CUSTOMIZATION: batched represented-variant price lookup. Tiles register their represented id via
    // `requestRepresentedPrice`; ids that arrive in the same tick coalesce into one `/resource/represented-prices`
    // call whose results update the price cells in place. This runs entirely on the client, AFTER first paint —
    // the loader is untouched, so the grid renders as fast as it does today and only the master prices refine.
    const [representedPrices, setRepresentedPrices] = useState<RepresentedPriceMap>(EMPTY_REPRESENTED_PRICES);
    // Ids we've already requested (dedupe across re-renders) and the pending batch not yet flushed.
    const requestedIdsRef = useRef<Set<string>>(new Set());
    const queuedIdsRef = useRef<Set<string>>(new Set());
    const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Keep the latest currency reachable from the flush closure without re-creating the callback each render.
    const currencyRef = useRef(currency);
    currencyRef.current = currency;

    const flushRepresentedPrices = useCallback(async () => {
        flushTimerRef.current = null;
        const batch = [...queuedIdsRef.current];
        queuedIdsRef.current = new Set();
        if (batch.length === 0) {
            return;
        }

        const params = new URLSearchParams({ ids: batch.join(',') });
        if (currencyRef.current) {
            params.set('currency', currencyRef.current);
        }

        try {
            const response = await fetch(`${resourceRoutes.representedPrices}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                throw new Error(`represented-prices ${response.status}`);
            }
            const { prices } = (await response.json()) as RepresentedPricesResult;
            setRepresentedPrices((prev) => {
                const next = { ...prev };
                for (const id of batch) {
                    // Present in the response -> real price; absent -> null so the tile falls back to the master price
                    // (rather than sitting on a skeleton forever).
                    next[id] = Object.hasOwn(prices, id) ? prices[id] : null;
                }
                return next;
            });
        } catch {
            // Network/endpoint failure: resolve the whole batch to null so every waiting tile falls back gracefully.
            setRepresentedPrices((prev) => {
                const next = { ...prev };
                for (const id of batch) {
                    next[id] = null;
                }
                return next;
            });
        }
    }, []);

    const requestRepresentedPrice = useCallback(
        (id: string) => {
            if (!enableRepresentedPrices || !id) {
                return;
            }
            // Dedupe the network request, but only skip *queuing* — never skip (re)scheduling below.
            if (!requestedIdsRef.current.has(id)) {
                requestedIdsRef.current.add(id);
                queuedIdsRef.current.add(id);
            }
            // Ensure a flush is scheduled whenever ids are still pending. Scheduling here (rather than only
            // when a new id is added) is what makes this survive React StrictMode's dev double-invoke: the
            // provider's unmount-cleanup clears the pending timer, then the tiles' effects re-run and re-call
            // this with already-requested ids — the batch is still queued, so we must re-arm the timer or the
            // flush would never fire and the price cells would sit on a skeleton forever.
            if (queuedIdsRef.current.size > 0 && flushTimerRef.current === null) {
                flushTimerRef.current = setTimeout(() => void flushRepresentedPrices(), 0);
            }
        },
        [enableRepresentedPrices, flushRepresentedPrices]
    );

    // NOTE: we deliberately do NOT clear the pending flush timer on unmount. A cleanup that cleared it
    // stranded the queued batch under React StrictMode's dev double-invoke (cleanup ran between the tiles'
    // register and the macrotask flush, so the fetch never fired and prices never updated). Letting the timer
    // fire is safe: the flush's `setRepresentedPrices` is a no-op on an unmounted provider in React 19, and the
    // endpoint is server-cached, so a rare wasted call on a fast unmount costs nothing.

    // Memoize the value so its reference changes only when a field consumers read actually changes. Without this,
    // every provider re-render mints a new object and re-renders every tile in the grid regardless of what changed.
    const value = useMemo(
        () => ({
            navigate,
            config,
            t,
            currency,
            getBadges,
            resolvedProductPaths: paths,
            enableRepresentedPrices,
            preferSearchRepresentedVariant,
            representedPrices,
            requestRepresentedPrice,
        }),
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- pathsKey stands in for the `paths` object identity
        [
            navigate,
            config,
            t,
            currency,
            getBadges,
            pathsKey,
            enableRepresentedPrices,
            preferSearchRepresentedVariant,
            representedPrices,
            requestRepresentedPrice,
        ]
    );

    return <ProductTileContext.Provider value={value}>{children}</ProductTileContext.Provider>;
}

/**
 * Hook that returns context if available, otherwise falls back to direct hook calls.
 * This allows ProductTile to work both inside and outside ProductTileProvider.
 */
// oxlint-disable-next-line react-refresh/only-export-components
export function useProductTileContext(): ProductTileContextValue {
    const context = useContext(ProductTileContext);
    if (context) {
        return context;
    }

    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const config = useConfig();
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const navigate = useNavigate();
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const { t } = useTranslation('product');
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const { currency } = useSite();
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    const getBadges = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) =>
            getProductBadges({ product, badgeDetails: config.global.badges, maxBadges: 2 }),
        [config.global.badges]
    );

    // Memoize the fallback value too, so consumers outside a provider get the same referential stability as those
    // inside one. Outside a provider there's no resolver map — tiles fall back to the slugify path — and represented
    // pricing is off (masters show the master price, exactly as today).
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
        () => ({
            navigate,
            config,
            t,
            currency,
            getBadges,
            resolvedProductPaths: EMPTY_PRODUCT_URL_MAP,
            enableRepresentedPrices: false,
            preferSearchRepresentedVariant: false,
            representedPrices: EMPTY_REPRESENTED_PRICES,
            requestRepresentedPrice: NOOP_REQUEST_REPRESENTED_PRICE,
        }),
        [navigate, config, t, currency, getBadges]
    );
}
